import { UserService } from './../../../common/service/user.service';
import { Component, inject, signal, computed } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { CustomerService } from '../../../common/service/customer.service';
import { TranslatePipe } from '@ngx-translate/core';
import { Constants } from '../../../common/helper/Constants';
import { Advisor } from '../../home/advisor/model/advisor';
import { environment } from '../../../environment/environment';
import { catchError, combineLatest, finalize, of, startWith, switchMap, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Panel } from 'primeng/panel';
import { CustomerCaseTableComponent } from '../customer-case-table/customer-case-table.component';
import { UserCaseTableComponent } from '../user-case-table/user-case-table.component';
import { Skeleton } from 'primeng/skeleton';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { Role } from '../../../core/auth/role.enum';
import { CsmUserProvisioningService } from '../csm-user-provisioning.service';
import { CsmProvisioningAction, CsmUserProvisioningRequest, CsmUserStatusResponse } from '../model/csm-user-provisioning';
import { NotificationService } from '../../../common/service/notification.service';


@Component({
  selector: 'gate-service-case-overview',
  standalone: true,
  imports: [ButtonModule, TranslatePipe, Panel, CustomerCaseTableComponent, UserCaseTableComponent, Skeleton],
  templateUrl: './service-case-overview.component.html',
  styleUrls: ['./service-case-overview.component.scss'] 
})
export class ServiceCaseOverviewComponent{
  // Inject necessary services
  private customerService = inject(CustomerService); // Service to fetch customer data
  private userService = inject(UserService);
  private httpClient = inject(HttpClient);
  private route = inject(ActivatedRoute);
  private locationService = inject(Location);
  private csmProvisioningService = inject(CsmUserProvisioningService);
  private notificationService = inject(NotificationService);
  // Signal to hold the location value
  public location = signal<string | undefined>('');
  public isShowingAllCases = signal<boolean>(false);
  public routes = Constants.routes;
  public advisor = signal<Advisor | undefined>(undefined);
  public userIncident = signal<string | null>(null);
  public customerIncident = signal<string | null>(null);
  public isLoading = signal<boolean>(false);
  public csmStatus = signal<CsmUserStatusResponse | null>(null);
  public isUnlocking = signal<boolean>(false);
  public role = Role;
  private userProfile = this.userService.getUserProfile();
  public isCompanyImpersonatedByAcpUser = computed(() => this.userService.isCompanyImpersonatedByAcpUser());

  constructor() {
    const isShowingAllCases = this.route.snapshot.params['isShowingAllCases'];
    if (isShowingAllCases) {
      this.isShowingAllCases.set(isShowingAllCases);
    }

    this.route.queryParams.subscribe(params => {
      const isShowingAllCases = params['isShowingAllCases'];
      if (isShowingAllCases){
      this.isShowingAllCases.set(isShowingAllCases);
      }
      this.locationService.replaceState(Constants.routes.serviceCase());
    });
    this.getCustomerAddress(); // Fetch customer address on initialization
    this.setCounters();
    this.loadCsmStatus();
  }

  /**
   * Loads the CSM provisioning status for the current customer's primary advisor.
   * Resolves the advisor contact number to build the externalId before requesting status.
   */
  public loadCsmStatus(): void {
    const customerNumber = this.userService.getCustomerNumber() ?? '';
    this.csmProvisioningService.getAdvisorExternalId(customerNumber).pipe(
      switchMap(({ externalId, advisor }) => {
        if (advisor) {
          this.advisor.set(advisor);
        }
        return this.csmProvisioningService.getStatus(externalId);
      }),
      catchError(() => of(null))
    ).subscribe(status => {
      this.csmStatus.set(status);
    });
  }

  public unlockCsm(): void {
    const customerNumber = this.userService.getCustomerNumber() ?? '';
    const externalId = this.csmStatus()?.externalId ?? '';
    const email =  this.csmStatus()?.email ??this.advisor()?.mailAddress ?? '';
    const payload: CsmUserProvisioningRequest = {
      actions: [CsmProvisioningAction.LoginAdd],
      externalId,
      customerNumber,
      email,
      requestedByName: this.userService.getUserName() ?? '',
      requestedByEmail: this.userService.getUserEmail() ?? '',
      requestedByGuid: this.userService.getUserSub() ?? ''
    };
    this.isUnlocking.set(true);
    this.csmProvisioningService.provisionUser(payload).pipe(
      finalize(() => this.isUnlocking.set(false))
    ).subscribe({
      next: (res) => {
        if (res.problems && res.problems.length > 0) {
          res.problems.forEach((problem: string) => {
            this.notificationService.showNotification('warn', 'Warning', problem);
          });
        }

        if (res.success) {
          this.loadCsmStatus();
        } else if (res.detail) {
          this.notificationService.showNotification('error', 'Error', res.detail);
        }
      },
      error: (err) => {
        const errorDetail = err?.error?.detail || err?.message || 'An error occurred';
        this.notificationService.showNotification('error', 'Error', errorDetail);
      }
    });
  }

  private haveGateCasesReadPermission(): boolean {
    return this.userProfile.permissions.some(perm => perm == this.role.GateCasesRead);
  }

  private haveGateCasesReadAllPermission(): boolean {
    return this.userProfile.permissions.some(perm => perm == this.role.GateCasesReadAll);
  }

  /**
   * Determines if user can view all service cases (customer cases)
   * Similar to incident logic - AccountManager or users with GateCasesReadAll permission
   */
  public canViewAllServiceCases = computed(() => {
    return this.isCompanyImpersonatedByAcpUser() || this.haveGateCasesReadAllPermission();
  });

  /**
   * Determines if user should see the service case section at all
   * User needs at least read permission for their own cases
   */
  public canViewServiceCases = computed(() => {
    return this.isCompanyImpersonatedByAcpUser() || this.haveGateCasesReadPermission() || this.haveGateCasesReadAllPermission();
  });

private setCounters(): void {
  const userMail = this.userService.getUserEmail() ?? '';
  this.isLoading.set(true);
  
  // User service cases - only if user has permission to view their own cases
  const userIncident$ = this.canViewServiceCases()
    ? this.httpClient
        .get<string>(`${environment.APIBase}Incident/userIncidents/count?email=${userMail}`)
        .pipe(
          startWith(null),
          catchError(() => of(null))
        )
    : of('0');

  // Customer service cases - only if user has permission to view all cases
  const customerIncident$ = this.canViewServiceCases()
    ? this.httpClient
        .get<string>(`${environment.APIBase}Incident/customerIncidents/count`)
        .pipe(
          startWith(null),
          catchError(() => of(null))
        )
    : of('0');

  combineLatest([userIncident$, customerIncident$])
    .pipe(
      tap(([userCount, customerCount]) => {
        this.userIncident.set(userCount);
        this.customerIncident.set(customerCount);
      }),
      finalize(() => this.isLoading.set(false))
    )
    .subscribe();
}

  /**
   * Fetches the customer's address using the CustomerService and updates the location signal.
   */
  private getCustomerAddress() {
    this.customerService.getLocation().then((res) => {
      // Update the location signal with the recipient's name from the response
      this.location.set(res);
    });
  }

}

